import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from './FAQAccordion';
import Seo from '../Seo';

const analyticsHeroImage = '/award-logos/Dataana1.png';
const analyticsSecondaryImage = '/award-logos/Dataana2.png';
const analyticsFallbackImage = '/award-logos/Dataana1.png';

const capabilities = [
  {
    title: 'Modern Data Engineering',
    desc: 'Engineer cloud-native ELT/ETL pipelines that ingest, clean, and unify disparate data sources into high-performance lakehouses or warehouses.'
  },
  {
    title: 'Descriptive & Diagnostic BI',
    desc: 'Deploy interactive executive dashboards and storytelling layers that uncover the "what" and "why" behind business performance trends.'
  },
  {
    title: 'Predictive modeling & AI',
    desc: 'Leverage machine learning and statistical modeling to forecast demand, identify churn, and optimize pricing through proactive intelligence.'
  },
  {
    title: 'Data Governance & Quality',
    desc: 'Establish rigorous data stewardship, metadata management, and automated quality checks to ensure a trusted single source of truth.'
  },
  {
    title: 'Big Data & Streaming',
    desc: 'Process high-velocity event streams and massive datasets using distributed computing frameworks for real-time operational visibility.'
  },
  {
    title: 'Master Data Management (MDM)',
    desc: 'Unify critical enterprise entities (customer, product, asset) across siloed systems to provide 360-degree visibility and data consistency.'
  }
];



const useCases = [
  { title: 'Customer 360 & Sentiment', desc: 'Aggregating touchpoints across CRM, Web, and Social to drive hyper-personalized engagement and churn reduction.' },
  { title: 'Supply Chain Resiliency', desc: 'Real-time inventory visibility and demand forecasting to optimize stock levels and reduce logistics bottlenecks.' },
  { title: 'Financial Intelligence', desc: 'Automated reconciliation, cash flow forecasting, and dynamic budgeting through integrated ERP data streams.' },
  { title: 'Operational Excellence', desc: 'IoT sensor data analysis and predictive maintenance to reduce equipment downtime and improve manufacturing yield.' }
];

const serviceModules = [
  {
    title: 'Data Strategy & Roadmap',
    points: ['Maturity assessment', 'Architecture blueprints', 'Governance frameworks']
  },
  {
    title: 'Cloud Data Warehousing',
    points: ['Snowflake/Databricks setup', 'Data Lakehouse design', 'Migration from legacy SQL']
  },
  {
    title: 'BI & Visualization',
    points: ['PowerBI/Tableau centers of excellence', 'Executive storytelling', 'Self-service enablement']
  },
  {
    title: 'Advanced Analytics (ML/AI)',
    points: ['Statistical modeling', 'Propensity & Churn analysis', 'Automated anomaly detection']
  }
];

const techStack = [
  { title: 'Warehousing & Lakes', items: ['Snowflake', 'Databricks', 'Azure Synapse', 'Google BigQuery'] },
  { title: 'Engineering / ETL', items: ['dbt', 'Apache Airflow', 'Fivetran', 'Matillion'] },
  { title: 'Visualization / BI', items: ['Power BI', 'Tableau', 'ThoughtSpot', 'Looker'] },
  { title: 'Data Governance', items: ['Collibra', 'Alation', 'Informatica', 'Atlan'] }
];

const industries = ['Banking & Finance', 'Healthcare', 'Retail & E-commerce', 'High-Tech', 'Energy & Utilities', 'Logistics'];

const executionSteps = [
  { phase: 'Discovery & Readiness Portfolio', desc: 'Audit existing data silos, quality levels, and technical debt to define a prioritized analytics roadmap.' },
  { phase: 'Architecture Design', desc: 'Develop modern data architecture (Mesh, Fabric, or Lakehouse) tailored to your operational scale and security needs.' },
  { phase: 'Pipeline Engineering', desc: 'Build automated ELT/ETL flows that ingest and transform raw data into governed, business-ready datasets.' },
  { phase: 'BI & Visualization Sprints', desc: 'Develop high-impact dashboards and storytelling layers using agile cycles with business stakeholders.' },
  { phase: 'Governance & MDM Setup', desc: 'Implement automated data quality gates, cataloging, and stewardship policies to ensure long-term data integrity.' },
  { phase: 'Scale & Value Realization', desc: 'Roll out self-service capabilities and advanced modeling to provide continuous business impact and ROI tracking.' }
];

const faqs = [
  {
    q: 'How does Libsys help organizations transition from "Data Silos" to a "Unified Insight" model?',
    a: 'We solve the silo problem through a combination of Modern Data Architecture and disciplined Engineering. We implement centralized Cloud Data Warehouses (like Snowflake or BigQuery) or Lakehouses (Databricks) that serve as a single source of truth. By building automated pipelines that ingest data from disparate ERP, CRM, and legacy systems, we consolidate your technical debt into a unified semantic layer. This allows every department to work from the same verified metrics, eliminating "different versions of the truth" during executive reviews.'
  },
  {
    q: 'What is the difference between a Data Lake and a Data Lakehouse?',
    a: 'A Data Lake is a repository for raw, unstructured data, which can often become a "Data Swamp" without proper management. A Data Lakehouse (like Databricks) combines the low-cost storage of a Data Lake with the high-performance transaction and governance features of a Data Warehouse. We typically recommend Lakehouse architectures for enterprises that need to support both high-speed BI reporting and advanced Machine Learning/AI workloads on the same unified platform, reducing architectural complexity and cost.'
  },
  {
    q: 'How do you ensure data security and regulatory compliance (GDPR/HIPAA)?',
    a: 'Data security is baked into our engineering process. We implement Role-Based Access Control (RBAC), data masking for PII (Personally Identifiable Information), and end-to-end encryption. Our governance frameworks include data lineage tracking, so you can always see exactly where a piece of data came from and how it was transformed. We also implement automated auditing to ensure that data usage aligns with global regulations, making you audit-ready at all times without manual effort.'
  },
  {
    q: 'Can Libsys help us enable "Self-Service BI" for non-technical users?',
    a: 'Yes, but self-service only works if the underlying data is clean and governed. Our approach is to build "Certified Data Models" where the complex logic (calculations, joins, aggregations) is handled in the warehouse and exposed as simple, drag-and-drop fields in tools like Power BI or Tableau. We then provide "Enablement Workshops" to help your business users understand how to build their own reports safely, ensuring they get the answers they need without accidentally creating inaccurate or un-governed metrics.'
  },
  {
    q: 'What role does "Data Governance" play in your delivery model?',
    a: 'Governance is the foundation of trust. Without it, analytics projects eventually lose credibility due to bad data. Our methodology includes establishing a "Data Dictionary" and "Stewardship Program" from day one. We implement automated data quality checks that alert your team if incoming data is missing or corrupted. This proactive approach ensures that by the time data reaches an executive dashboard, it has been verified for accuracy, completeness, and timeliness.'
  },
  {
    q: 'What is the typical timeline to see a ROI from an Enterprise Data initiative?',
    a: 'We focus on "Agile Value Realization." While a full enterprise data transformation is a long-term journey, we aim to deliver the first high-impact "Use Case"—such as a unified Sales or Inventory dashboard—within 8-12 weeks. This allows you to see measurable business impact (like reduced stockouts or improved margin visibility) early in the project. We then use the ROI from these early wins to fund and prioritize the next phases of your data maturity roadmap.'
  }
];

const ServiceDataAnalytics = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen pt-8 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="Data Analytics Services"
        description="Libsys data analytics services — descriptive, diagnostic, and predictive analytics. Transform enterprise data into decisions with BI, reporting, and modeling platforms."
        path="/services/data-analytics"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="lg:col-span-5"
          >
            <span className="inline-flex items-center gap-2 text-strategic-tagline mb-5">
              Intelligence & Insights
            </span>
            <h1 className="text-h1 text-primary mb-5">
              Enterprise Data Analytics: Unifying the Modern Data Stack
            </h1>
            <p className="text-slate-600 text-body leading-[1.7] font-medium mb-6 max-w-2xl">
              Libsys transforms fragmented raw data into a strategic business asset. We build end-to-end data ecosystems that unify siloed systems into a single source of truth, empowering your leadership with real-time visibility and predictive power. From engineering scalable cloud data warehouses to deploying high-impact visualization layers, we bridge the gap between complex technical infrastructure and actionable executive insights. By establishing rigorous data stewardship and automated quality checks, we ensure that every business decision is governed by accurate and timely intelligence.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-8">
              {[
                'Unified Cloud Data Warehousing',
                'Agile Business Intelligence (BI)',
                'Automated Data Engineering (ETL)',
                'Strategic Data Governance & MDM',
                'Advanced Predictive Modeling',
                'Real-time Operational Analytics'
              ].map((item) => (
                <li key={item} className="text-slate-700 text-subtext font-bold leading-relaxed flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+16307991556"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-[#254b85] text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-blue-900/10 active:scale-95 group"
              >
                Request Analytics Roadmap
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 lg:-mt-12"
          >
            <div className="rounded-[2.5rem] overflow-hidden border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group">
              <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
              <img
                src={analyticsHeroImage}
                alt="Data Visualization Simulation"
                className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000"
                onError={(e) => { e.currentTarget.src = analyticsFallbackImage; }}
              />
            </div>
          </motion.div>
        </div>
        </div>
      </section>

      {/* ══ CORE CAPABILITIES ════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14 text-center">
            <span className="text-strategic-tagline block mb-2">Technical Depth</span>
            <h2 className="text-h2 text-primary">Advanced Capabilities Across the Data Lifecycle</h2>
            <p className="text-slate-600 text-body max-w-3xl mx-auto mt-4 leading-relaxed">
              Our analytics practice integrates high-precision engineering with strategic business context, ensuring that insights perform reliably in high-stakes enterprise environments.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((item) => (
              <div key={item.title} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-[22px] font-black text-primary mb-4 leading-tight">{item.title}</h3>
                <p className="text-slate-600 text-subtext leading-relaxed font-medium">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SERVICE PORTFOLIO ════════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            <div className="lg:col-span-8 p-8 lg:p-12">
              <span className="text-strategic-tagline block mb-3">Service Portfolio</span>
              <h2 className="text-h2 text-primary mb-6">Strategic Analytics Service Modules</h2>
              <p className="text-slate-600 text-body mb-8 max-w-3xl">
                We provide end-to-end data services with clear roadmap ownership so programs can scale from initial proof-of-value to unified enterprise deployment without losing governance or business alignment.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {serviceModules.map((module) => (
                  <div key={module.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="text-body font-black text-primary mb-4 flex items-center gap-2">
                       {module.title}
                    </h3>
                    <ul className="space-y-3">
                      {module.points.map((point) => (
                        <li key={point} className="text-slate-600 text-subtext font-semibold leading-relaxed flex items-start gap-2.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-100 relative overflow-hidden">
               <img src={analyticsSecondaryImage} alt="Data Delivery Pipeline" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = analyticsFallbackImage; }} />
               <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent flex flex-col justify-end p-8">
                  <p className="text-white text-lg font-black leading-tight italic">"Bridging the gap between raw datasets and executive decision confidence."</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ DELIVERY PHASES ══════════════════════════════════════════════════ */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-12 mb-10">
              <span className="text-strategic-tagline block mb-3">Implementation Framework</span>
              <h2 className="text-h2 text-primary mb-6">A Disciplined Path to Data Maturity</h2>
              <p className="text-slate-600 text-body max-w-4xl">
                 Success in analytics requires more than just dashboards; it requires a structured lifecycle that manages data quality, technical risk, and organizational adoption in lockstep.
              </p>
            </div>
            <div className="lg:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
                {executionSteps.map((step, idx) => (
                  <div key={step.phase} className="relative pl-12">
                    <div className="absolute left-0 top-0 text-5xl font-black text-slate-200 leading-none">
                      {(idx + 1).toString().padStart(2, '0')}
                    </div>
                    <div className="relative">
                      <h4 className="text-body font-black text-primary mb-2 uppercase tracking-wide">{step.phase}</h4>
                      <p className="text-slate-600 text-subtext leading-relaxed font-medium">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TECH STACK & USE CASES ════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
             <span className="text-strategic-tagline block mb-3 text-center lg:text-left">Technology Ecosystem</span>
             <h2 className="text-h2 text-primary mb-6 text-center lg:text-left">The Modern Data Stack</h2>
             <p className="text-slate-600 text-body mb-10 max-w-4xl text-center lg:text-left mx-auto lg:mx-0">
                We leverage a specialized, hybrid tech-stack that connects proven cloud environments with the latest in pipeline orchestration to ensure your data is performant, portable, and secure. Our engineers prioritize tools that offer high-precision ETL and zero-trust data access.
             </p>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {techStack.map(cat => (
                  <div key={cat.title} className="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:shadow-lg transition-all">
                     <h5 className="font-black text-primary text-tiny uppercase tracking-widest mb-3">{cat.title}</h5>
                     <div className="flex flex-wrap gap-2">
                        {cat.items.map(i => (
                          <span key={i} className="px-2.5 py-1 bg-white border border-slate-200 rounded-md text-tiny font-bold text-slate-600">{i}</span>
                        ))}
                     </div>
                  </div>
                ))}
             </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch pt-12 border-t border-slate-100">
             <div className="space-y-6">
                <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm h-full">
                   <h3 className="text-xl font-black text-primary mb-6">Strategic Use-Cases</h3>
                   <div className="space-y-6">
                      {useCases.map(uc => (
                         <div key={uc.title} className="border-b border-slate-100 pb-6 last:border-b-0 last:pb-0">
                            <h4 className="text-primary font-black text-subtext uppercase tracking-wide mb-2">{uc.title}</h4>
                            <p className="text-slate-600 text-subtext font-medium leading-relaxed">{uc.desc}</p>
                         </div>
                      ))}
                   </div>
                </div>
             </div>

             <div className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                   <h3 className="text-xl font-black text-primary mb-6">Enterprise Insight Benchmarks</h3>
                   <div className="space-y-4">
                      {[
                        'Automated financial reconciliation across global regions.',
                        'Real-time inventory visibility for complex supply chains.',
                        'Intelligent churn prediction for high-volume consumer apps.',
                        'Secure master data management for multi-entity holdings.'
                      ].map(b => (
                        <div key={b} className="flex items-start gap-4">
                           <div className="w-2 h-2 rounded-full bg-secondary mt-2.5 flex-shrink-0" />
                           <p className="text-slate-600 text-body font-medium leading-relaxed">{b}</p>
                        </div>
                      ))}
                   </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8">
                   <h3 className="text-[17px] font-black text-primary mb-4">Target Sectors</h3>
                   <div className="flex flex-wrap gap-2">
                     {industries.map(ind => (
                       <span key={ind} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-subtext font-bold text-slate-600 shadow-sm">{ind}</span>
                     ))}
                   </div>
                </div>
             </div>
          </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default ServiceDataAnalytics;
